import { pagedQuery, toPaged, type IPagedCollection } from '@moko/utils';
import { internalFetch } from '../utils/fetchHelper';

export interface ISettings {
  clientUrl: string;
}

export const getSettings = async (
  abortController: AbortController
): Promise<ISettings> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/config`,
    'GET',
    abortController
  );
};

export const getBuildNumber = async (
  abortController: AbortController
): Promise<string> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/buildNumber`,
    'GET',
    abortController
  );
};

export const resetCache = async (
  abortController: AbortController
): Promise<any> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/resetCache`,
    'POST',
    abortController
  );
};

export interface IPricingTier {
  id?: number;
  code: string;
  name: string;
  discountPercentage: number;
  minOrderCount?: number | null;
  sortOrder: number;
}

export const getPricingTiers = async (
  abortController: AbortController
): Promise<IPricingTier[]> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/pricingTiers`,
    'GET',
    abortController
  );
};

export interface IWorkType {
  id?: number;
  code: string;
  name: string;
  sortOrder: number;
}

export const getTenantWorkTypes = async (
  abortController: AbortController,
  tenantId?: number
): Promise<IWorkType[]> => {
  const tenant =
    tenantId != null && Number.isFinite(tenantId)
      ? `?tenantId=${tenantId}`
      : '';
  const result = await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/tenant/workTypes${tenant}`,
    'GET',
    abortController
  );
  return Array.isArray(result) ? result : [];
};

export const getWorkTypes = async (
  abortController: AbortController
): Promise<IWorkType[]> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/workTypes`,
    'GET',
    abortController
  );
};

export interface IBillingClass {
  id?: number;
  code: string;
  name: string;
  description?: string | null;
}

export interface IBillingClassBody {
  code?: string | null;
  name?: string | null;
  description?: string | null;
}

const billingClassUrl = (billingClassId: number) =>
  `${import.meta.env.VITE_BACKEND_API_URL}/p/billingClass?${new URLSearchParams(
    {
      billingClassId: String(billingClassId),
    }
  )}`;

export const getBillingClassesPaged = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string,
  filters?: string
): Promise<IPagedCollection<IBillingClass>> =>
  toPaged<IBillingClass>(
    await internalFetch(
      `${import.meta.env.VITE_BACKEND_API_URL}/p/billingClasses?${pagedQuery({
        pageSize,
        pageNo,
        orderBy,
        search,
        filters,
      })}`,
      'GET',
      abortController
    ),
    pageSize,
    pageNo
  );

export const getBillingClasses = async (
  abortController: AbortController
): Promise<IBillingClass[]> =>
  (await getBillingClassesPaged(abortController, 25, 1)).items;

export const getBillingClass = async (
  abortController: AbortController,
  billingClassId: number
): Promise<IBillingClass> =>
  internalFetch(billingClassUrl(billingClassId), 'GET', abortController);

export const createBillingClass = async (
  abortController: AbortController,
  body: IBillingClassBody
): Promise<unknown> =>
  internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/billingClass`,
    'POST',
    abortController,
    JSON.stringify(body)
  );

export const updateBillingClass = async (
  abortController: AbortController,
  billingClassId: number,
  body: IBillingClassBody
): Promise<unknown> =>
  internalFetch(
    billingClassUrl(billingClassId),
    'PUT',
    abortController,
    JSON.stringify(body)
  );

export const deleteBillingClass = async (
  abortController: AbortController,
  billingClassId: number
): Promise<unknown> =>
  internalFetch(billingClassUrl(billingClassId), 'DELETE', abortController);

export interface ICertificationType {
  id?: number;
  code: string;
  name: string;
  sortOrder: number;
}

export const getCertificationTypes = async (
  abortController: AbortController
): Promise<ICertificationType[]> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/certificationTypes`,
    'GET',
    abortController
  );
};

export interface IWorkTypeCertification {
  workTypeId: number;
  certificationTypeId: number;
}

const positiveId = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const getWorkTypeCertifications = async (
  abortController: AbortController
): Promise<IWorkTypeCertification[]> => {
  const result = await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/workTypeCertifications`,
    'GET',
    abortController
  );
  const rows = Array.isArray(result)
    ? result
    : Array.isArray(result?.items)
    ? result.items
    : [];
  const seen = new Set<string>();
  return rows.flatMap(
    (row: { workTypeId?: unknown; certificationTypeId?: unknown }) => {
      const workTypeId = positiveId(row?.workTypeId);
      const certificationTypeId = positiveId(row?.certificationTypeId);
      if (!workTypeId || !certificationTypeId) return [];
      const key = `${workTypeId}:${certificationTypeId}`;
      if (seen.has(key)) return [];
      seen.add(key);
      return [{ workTypeId, certificationTypeId }];
    }
  );
};

export const getGlobalCancellationPolicy = (
  controller: AbortController
): Promise<{ key: string; value: string }> =>
  internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/globalSettings/cancellationPolicy`,
    'GET',
    controller
  );

export const updateGlobalCancellationPolicy = (
  controller: AbortController,
  noticeHours: number
): Promise<void> =>
  internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/globalSettings/cancellationPolicy`,
    'PUT',
    controller,
    JSON.stringify({ noticeHours })
  );
